import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import {
  formatComptabiliteMoney,
  formatComptabiliteDate,
  formatComptabiliteDateTime,
  getComptabiliteDatePickerLocale } from './utils/comptabiliteSaudiFormat';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, AlertTriangle, BookOpen, Calculator, Calendar, CheckCircle, ChevronDown, ChevronUp, Clock, CreditCard, DollarSign, Download, Edit, Edit3, Eye, FileOutput, FileSpreadsheet, FileText, Filter, Hash, Info, Layers, LayoutGrid, LayoutList, Link, Link2, List, Loader2, PieChart, Play, Plus, RefreshCcw, Save, Search, Settings, Shield, ShoppingCart, Target, Trash2, Unlink, Upload, User, X, XCircle, Zap } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { logger } from '../../shared/utils/logger';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import * as XLSX from 'xlsx';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import {
  getJournalEntries,
  getJournalEntry,
  deleteJournalEntry,
  transformJournalEntry,
  createJournalEntry,
  updateJournalEntry,
  getChartOfAccounts,
  applyAutoLettrage,
  validateEntry as validateEntryApi,
  getEventLog,
  getEventLogStats } from '../../shared/utils/comptabiliteService';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useComptabiliteConfig } from './useComptabiliteConfig';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { buildChartAccountOptions, CHART_ACCOUNTS_REFRESH_EVENT } from './utils/chartAccountOptions';
import {
  loadAnalyticalSelectOptions,
  ANALYTICAL_CODES_REFRESH_EVENT,
} from './utils/comptabiliteAnalyticalOptions';
import { fetchCurrentFiscalYearContext } from './utils/fiscalYearUtils';
import { mergeStickySelectClass, QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('fr', fr);
registerLocale('ar', arLocale);

// --- Inlined: EntryValidator ---
const isEntryEmpty = (entry) => {
  if (!entry) return true;
  const hasDate = Boolean(entry.date || entry.entry_date);
  const hasReference = Boolean(entry.reference?.trim());
  const hasDescription = Boolean(entry.description?.trim());
  const hasLines = Array.isArray(entry.lines) && entry.lines.length > 0;
  return !hasDate && !hasReference && !hasDescription && !hasLines;
};

/** Only call backend when the form has enough data for a meaningful validation */
const isEntryReadyForValidation = (entry) => {
  if (!entry) return false;
  const hasDate = Boolean(entry.date || entry.entry_date);
  const hasReference = Boolean(entry.reference?.trim());
  const hasDescription = Boolean(entry.description?.trim());
  const lines = Array.isArray(entry.lines) ? entry.lines : [];
  const activeLines = lines.filter(
    (line) =>
      (line.account || line.account_number) &&
      (parseFloat(line.debit) > 0 || parseFloat(line.credit) > 0)
  );
  if (!hasDate || !hasReference || !hasDescription || activeLines.length === 0) {
    return false;
  }
  const totalDebit = lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
  return Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;
};

const hasLineAmount = (line) => parseFloat(line?.debit) > 0 || parseFloat(line?.credit) > 0;

const isValidAccountNumber = (value) => {
  const account = String(value || '').trim();
  return Boolean(account) && account !== '—' && account !== '-' && account !== '–';
};

const buildBalancedLinePair = (line) => {
  const amount = parseFloat(line.debit) || parseFloat(line.credit);
  if (!amount || amount <= 0) return null;
  const creditAccount = line.auxiliary_code?.trim();
  if (!line.account?.trim() || !creditAccount || !line.label?.trim()) return null;
  return [
    { ...line, debit: amount, credit: '' },
    {
      ...line,
      account: creditAccount,
      auxiliary_code: '',
      debit: '',
      credit: amount },
  ];
};

const appendPendingLine = (lines, pendingLine) => {
  const pair = buildBalancedLinePair(pendingLine);
  if (pair) return [...lines, ...pair];
  const amount = parseFloat(pendingLine.debit) || parseFloat(pendingLine.credit);
  return [...lines, { ...pendingLine, debit: amount, credit: '' }];
};

const previewPendingLines = (pendingLine) => {
  if (!hasLineAmount(pendingLine)) return [];
  const pair = buildBalancedLinePair(pendingLine);
  if (pair) return pair;
  const amount = parseFloat(pendingLine.debit) || parseFloat(pendingLine.credit);
  return [{ ...pendingLine, debit: amount, credit: '' }];
};

const computeEntryLineTotals = (lines = [], pendingLine = null) => {
  const pendingPreview = previewPendingLines(pendingLine);
  const allLines = pendingPreview.length ? [...lines, ...pendingPreview] : lines;
  const totalDebit = allLines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
  const totalCredit = allLines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
  const diff = totalDebit - totalCredit;
  return { totalDebit, totalCredit, diff, isBalanced: Math.abs(diff) < 0.01, includesPending: pendingPreview.length > 0 };
};

const resolveEntryLinesForSave = (savedLines, pendingLine, p) => {
  let lines = [...savedLines];
  if (hasLineAmount(pendingLine)) {
    if (!pendingLine.account?.trim() || !pendingLine.label?.trim()) {
      return {
        ok: false,
        code: 'pending_incomplete',
        message: p('error_pending_incomplete') };
    }
    lines = appendPendingLine(lines, pendingLine);
  }
  if (!lines.length) {
    return { ok: false, code: 'no_lines', message: p('error_no_lines') };
  }
  const invalidLineIndex = lines.findIndex((line) => !isValidAccountNumber(line.account || line.account_number));
  if (invalidLineIndex !== -1) {
    return {
      ok: false,
      code: 'invalid_account',
      message: p('error_invalid_account', { li: invalidLineIndex + 1 }) };
  }
  const totals = computeEntryLineTotals(lines);
  if (!totals.isBalanced) {
    return {
      ok: false,
      code: 'unbalanced',
      message: p('error_unbalanced', {
        debit: formatComptabiliteMoney(totals.totalDebit),
        credit: formatComptabiliteMoney(totals.totalCredit),
        diff: formatComptabiliteMoney(Math.abs(totals.diff)),
        side: totals.diff > 0 ? p('error_side_credit') : p('error_side_debit') }),
      totals };
  }
  return { ok: true, lines, totals };
};

const toValidatorPayload = (entry) => ({
  entry_date: entry.date || entry.entry_date || '',
  reference: entry.reference || '',
  description: entry.description || '',
  folio: entry.folio || '',
  piece_number: entry.piece_number || '',
  date_document: entry.date_document || '',
  date_echeance: entry.date_echeance || '',
  lines: (entry.lines || []).map((line) => ({
    account: line.account || line.account_number || '',
    account_number: line.account_number || line.account || '',
    label: line.label || line.description || '',
    description: line.description || line.label || '',
    debit: line.debit || 0,
    credit: line.credit || 0,
    date_echeance: line.date_echeance || '',
    lettrage: line.lettrage || line.lettering_code || '' })) });

/**
 * Real-time Entry Validator Component
 * Shows validation status while user types
 */
const EntryValidator = ({ entry, journalId, onValidationChange }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.ecrituresComptablesPage.${key}`, opts);
  const [validationStatus, setValidationStatus] = useState('pending');
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  const runValidation = useCallback(async () => {
    if (!entry || !journalId) return;

    if (isEntryEmpty(entry) || !isEntryReadyForValidation(entry)) {
      setValidationStatus('pending');
      setErrors([]);
      setWarnings([]);
      onValidationChange?.({ isValid: false, status: 'pending', errors: [], warnings: [] });
      return;
    }

    setIsValidating(true);
    try {
      const result = await validateEntryApi(toValidatorPayload(entry), journalId);
      setValidationStatus(result.status || (result.is_valid ? 'valid' : 'error'));
      setErrors(result.errors || []);
      setWarnings(result.warnings || []);
      onValidationChange?.({
        isValid: result.is_valid,
        status: result.status,
        errors: result.errors,
        warnings: result.warnings });
    } catch (error) {
      // Don't block the form with backend connectivity banners while typing
      logger.error('Validation error:', error);
      setValidationStatus('pending');
      setErrors([]);
      setWarnings([]);
      onValidationChange?.({ isValid: false, status: 'pending', errors: [], warnings: [] });
    } finally {
      setIsValidating(false);
    }
  }, [entry, journalId, onValidationChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      runValidation();
    }, 500);
    return () => clearTimeout(timer);
  }, [runValidation]);

  const getStatusIcon = () => {
    if (isValidating) return <Loader2 size={18} className="animate-spin text-blue-400" />;

    switch (validationStatus) {
      case 'valid':
        return <CheckCircle size={18} className="text-green-400" />;
      case 'warning':
        return <AlertTriangle size={18} className="text-yellow-400" />;
      case 'error':
        return <XCircle size={18} className="text-red-400" />;
      default:
        return <Loader2 size={18} className="text-white/40" />;
    }
  };

  const getStatusText = () => {
    if (isValidating) return p('validation_pending');

    switch (validationStatus) {
      case 'valid':
        return p('validation_valid');
      case 'warning':
        return p('validation_warnings', { count: warnings.length });
      case 'error':
        return p('validation_errors', { count: errors.length });
      default:
        return p('validation_waiting');
    }
  };

  const getStatusColor = () => {
    switch (validationStatus) {
      case 'valid':
        return 'bg-green-500/20 border-green-400/30 text-green-300';
      case 'warning':
        return 'bg-yellow-500/20 border-yellow-400/30 text-yellow-300';
      case 'error':
        return 'bg-red-500/20 border-red-400/30 text-red-300';
      default:
        return 'bg-white/5 border-white/20 text-white/60';
    }
  };

  return (
    <div className="space-y-3">
      {validationStatus !== 'pending' && (
        <div className={`px-4 py-2 rounded-xl border ${getStatusColor()} flex items-center space-x-2 transition-all duration-300`}>
          {getStatusIcon()}
          <span className="text-sm font-medium">{getStatusText()}</span>
        </div>
      )}

      <AnimatePresence>
        {errors.length > 0 && errors.some((e) => e.type !== 'system') && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-red-500/10 border border-red-400/30 rounded-xl p-4 space-y-2"
          >
            <div className="flex items-center space-x-2 mb-2">
              <XCircle size={16} className="text-red-400" />
              <span className="text-red-300 font-semibold text-sm">{p('errors_detected')}</span>
            </div>
            {errors.filter((error) => error.type !== 'system').map((error, index) => (
              <div key={index} className="flex items-start space-x-2 text-sm">
                <span className="text-red-400 font-mono">⬢</span>
                <div>
                  {error.line && <span className="text-red-300 font-semibold">{p('line_prefix', { line: error.line })} </span>}
                  <span className="text-white/80">{error.message}</span>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {warnings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-yellow-500/10 border border-yellow-400/30 rounded-xl p-4 space-y-2"
          >
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle size={16} className="text-yellow-400" />
              <span className="text-yellow-300 font-semibold text-sm">{p('warnings_label')}</span>
            </div>
            {warnings.map((warning, index) => (
              <div key={index} className="flex items-start space-x-2 text-sm">
                <span className="text-yellow-400 font-mono">⬢</span>
                <div>
                  {warning.line && <span className="text-yellow-300 font-semibold">{p('line_prefix', { line: warning.line })} </span>}
                  <span className="text-white/80">{warning.message}</span>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Inlined: EntryTemplatesManager ---
const EntryTemplatesManager = ({ isOpen, onClose, onSelectTemplate }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.ecrituresComptablesPage.${key}`, opts);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState({
    template_name: '',
    family: 'BANQUE',
    description: '',
    journal_type: 'BQ',
    lines: []
  });

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      // Will connect to API
      // For now, show built-in templates
      setTemplates([
        { id: 'tpl_bank_payment', template_name: p('tpl_bank_payment'), family: 'BANQUE', use_count: 45 },
        { id: 'tpl_bank_receipt', template_name: p('tpl_bank_receipt'), family: 'BANQUE', use_count: 78 },
        { id: 'tpl_salary', template_name: p('tpl_salary'), family: 'BANQUE', use_count: 12 },
        { id: 'tpl_purchase', template_name: p('tpl_purchase'), family: 'VENTE', use_count: 34 },
        { id: 'tpl_sale', template_name: p('tpl_sale'), family: 'VENTE', use_count: 89 }
      ]);
    } catch (error) {
      logger.error('Error loading templates:', error);
    }
  };

  const handleGenerate = async (template) => {
    setIsGenerating(true);
    try {
      // Will call backend API to generate entry from template
      // For now, just pass template to parent
      onSelectTemplate && onSelectTemplate(template);
      setIsGenerating(false);
      onClose();
    } catch (error) {
      logger.error('Error generating from template:', error);
      setIsGenerating(false);
    }
  };

  const familyIcons = {
    BANQUE: CreditCard,
    VENTE: ShoppingCart,
    PRESTATION: DollarSign
  };

  const familyColors = {
    BANQUE: 'from-blue-500 to-blue-600',
    VENTE: 'from-green-500 to-green-600',
    PRESTATION: 'from-purple-500 to-purple-600'
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-gradient-to-br from-slate-800 via-blue-900/50 to-slate-800 border-2 border-blue-400/50 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.3)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{p('tpl_title')}</h2>
              <p className="text-white/60 text-sm">{p('tpl_subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {templates.map((template) => {
              const FamilyIcon = familyIcons[template.family] || FileText;
              const familyColor = familyColors[template.family] || 'from-gray-500 to-gray-600';

              return (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-blue-400/40 transition-all duration-300 cursor-pointer group"
                  onClick={() => handleGenerate(template)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${familyColor} group-hover:scale-110 transition-transform`}>
                      <FamilyIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <Zap size={14} className="text-yellow-400" />
                      <span className="text-xs text-white/60">{template.use_count}</span>
                    </div>
                  </div>

                  <h3 className="text-white font-semibold text-sm mb-1">{template.template_name}</h3>
                  <p className="text-white/50 text-xs mb-3">{template.family}</p>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerate(template);
                    }}
                    disabled={isGenerating}
                    className="w-full bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-300 px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        <span>{p('tpl_generating')}</span>
                      </>
                    ) : (
                      <>
                        <Zap size={14} />
                        <span>{p('tpl_use')}</span>
                      </>
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Empty state */}
          {templates.length === 0 && (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto mb-4 text-white/20" />
              <p className="text-white/60">{p('tpl_none')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
          <button
            onClick={() => setShowEditor(true)}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-xl font-medium hover:from-green-600 hover:to-green-700 transition-all flex items-center space-x-2"
          >
            <Plus size={16} />
            <span>{p('tpl_new')}</span>
          </button>

          <p className="text-white/50 text-sm">{p('tpl_count', { count: templates.length })}</p>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

// --- Inlined: JournalElectroniqueViewer ---
const DEFAULT_JOURNAL_EVENTS = [
  { id: 'j1', event_date: '2024-12-28', event_time: '14:32:00', event_type: 'period_close', event_category: 'periode', descKey: 'demo_event_period_close', userNameKey: 'demo_user_accountant', is_critical: 1 },
  { id: 'j2', event_date: '2024-12-20', event_time: '09:15:00', event_type: 'export', event_category: 'rapport', descKey: 'demo_event_export', userNameKey: 'demo_user_daf', is_critical: 0 },
  { id: 'j3', event_date: '2024-12-15', event_time: '16:48:00', event_type: 'parameter_change', event_category: 'parametres', descKey: 'demo_event_tva_change', userNameKey: 'demo_user_admin', is_critical: 1 },
  { id: 'j4', event_date: '2024-12-15', event_time: '02:00:00', event_type: 'backup_create', event_category: 'securite', descKey: 'demo_event_backup', userNameKey: 'system_user', is_critical: 0 },
  { id: 'j5', event_date: '2024-12-10', event_time: '11:22:00', event_type: 'entry_validate', event_category: 'ecriture', descKey: 'demo_event_entry_validate', userNameKey: 'demo_user_accountant', is_critical: 0 },
];

const getEventDisplayDate = (event, language) => {
  if (event.event_timestamp) return formatComptabiliteDateTime(event.event_timestamp, language);
  if (event.event_date) return `${event.event_date}${event.event_time ? ` ${event.event_time}` : ''}`;
  return '—';
};

const JournalElectroniqueViewer = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.ecrituresComptablesPage.${key}`, opts);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    eventType: '',
    category: '',
    userId: '',
    criticalOnly: false,
    searchTerm: ''
  });

  const eventTypes = [
    { value: '', label: p('event_type_all') },
    { value: 'entry_create', label: p('event_type_entry_create') },
    { value: 'entry_modify', label: p('event_type_entry_modify') },
    { value: 'entry_delete', label: p('event_type_entry_delete') },
    { value: 'entry_validate', label: p('event_type_entry_validate') },
    { value: 'period_close', label: p('event_type_period_close') },
    { value: 'period_reopen', label: p('event_type_period_reopen') },
    { value: 'account_create', label: p('event_type_account_create') },
    { value: 'account_modify', label: p('event_type_account_modify') },
    { value: 'parameter_change', label: p('event_type_parameter_change') },
    { value: 'backup_create', label: p('event_type_backup_create') },
    { value: 'user_login', label: p('event_type_user_login') },
    { value: 'user_logout', label: p('event_type_user_logout') },
    { value: 'export', label: p('event_type_export') }
  ];

  const categories = [
    { value: '', label: p('category_all') },
    { value: 'ecriture', label: p('category_ecriture') },
    { value: 'journal', label: p('category_journal') },
    { value: 'compte', label: p('category_compte') },
    { value: 'periode', label: p('category_periode') },
    { value: 'securite', label: p('category_securite') },
    { value: 'parametres', label: p('category_parametres') },
    { value: 'rapport', label: p('category_rapport') }
  ];

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, filters]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [eventsData, statsData] = await Promise.all([
        getEventLog(filters),
        getEventLogStats(filters.startDate, filters.endDate)
      ]);
      setEvents(eventsData?.length ? eventsData : DEFAULT_JOURNAL_EVENTS);
      setStats(statsData && Object.keys(statsData).length ? statsData : {
        total: (eventsData?.length || DEFAULT_JOURNAL_EVENTS.length),
        critical: (eventsData?.length ? eventsData : DEFAULT_JOURNAL_EVENTS).filter((e) => e.is_critical).length,
        users: 3,
        categories: 5 });
    } catch (error) {
      logger.error('Error loading event log:', error);
    }
    setIsLoading(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const filteredEvents = events.filter(event => {
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      return (
        event.description?.toLowerCase().includes(searchLower) ||
        event.user_name?.toLowerCase().includes(searchLower) ||
        event.event_type?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });
  const paginatedEvents = filteredEvents;

  const exportToExcel = () => {
    const exportData = filteredEvents.map(event => ({
      [p('col_datetime')]: formatComptabiliteDateTime(event.event_timestamp, i18n.language),
      [p('col_type')]: event.event_type,
      [p('col_category')]: event.category,
      [p('col_description')]: event.descKey ? p(event.descKey) : event.description,
      [p('col_user')]: event.userNameKey ? p(event.userNameKey) : event.user_name,
      [p('col_critical')]: event.is_critical ? p('export_yes') : p('export_no'),
      [p('export_col_details')]: event.details
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, p('export_sheet_name'));
    XLSX.writeFile(wb, `journal_electronique_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getCategoryColor = (category) => {
    const cat = category || 'other';
    const colors = {
      ecriture: 'bg-blue-500/20 text-blue-300',
      journal: 'bg-green-500/20 text-green-300',
      compte: 'bg-purple-500/20 text-purple-300',
      periode: 'bg-orange-500/20 text-orange-300',
      securite: 'bg-red-500/20 text-red-300',
      parametres: 'bg-yellow-500/20 text-yellow-300',
      rapport: 'bg-indigo-500/20 text-indigo-300' };
    return colors[cat] || 'bg-white/10 text-white/70';
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1280px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-400" />
            <div>
              <h2 className="text-2xl font-bold text-white">{p('journal_title')}</h2>
              <p className="text-sm text-white/60 mt-1">{p('journal_subtitle')}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/70 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {stats && Object.keys(stats).length > 0 && (
          <div className="p-4 border-b border-white/10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 shrink-0">
            {[
              { label: p('stat_total_events'), value: stats.total || 0, icon: Activity, color: 'from-blue-500 to-cyan-600' },
              { label: p('stat_critical_events'), value: stats.critical || 0, icon: AlertTriangle, color: 'from-red-500 to-pink-600' },
              { label: p('stat_active_users'), value: stats.users || 0, icon: User, color: 'from-green-500 to-emerald-600' },
              { label: p('stat_categories'), value: stats.categories || 0, icon: PieChart, color: 'from-purple-500 to-pink-600' },
            ].slice(0, 4).map((stat, i) => (
              <div key={i} className="relative group">
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-2xl opacity-0 group-hover:opacity-40 transition-opacity blur-sm`} style={{ pointerEvents: 'none' }} />
                <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
                  <div className="flex items-center gap-2 mb-1">
                    <stat.icon className="w-4 h-4 text-white/70" />
                    <span className="text-xs text-white/60">{stat.label}</span>
                  </div>
                  <div className="text-xl font-bold text-white">{stat.value}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="p-4 border-b border-white/10 space-y-4 shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('filter_start_date')}</label>
              <input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white" />
            </div>
            <div>
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('filter_end_date')}</label>
              <input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white" />
            </div>
            <div>
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('filter_event_type')}</label>
              <CustomSelect value={filters.eventType} onChange={(v) => handleFilterChange('eventType', v)} options={eventTypes} autoSelect={false} />
            </div>
            <div>
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('filter_category')}</label>
              <CustomSelect value={filters.category} onChange={(v) => handleFilterChange('category', v)} options={categories} autoSelect={false} />
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input type="text" placeholder={p('search_events')} value={filters.searchTerm} onChange={(e) => handleFilterChange('searchTerm', e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-white/80 text-sm">
              <input type="checkbox" checked={filters.criticalOnly} onChange={(e) => handleFilterChange('criticalOnly', e.target.checked)} className="rounded accent-red-500" />
              <AlertTriangle className="w-4 h-4 text-red-400" />
              {p('critical_only')}
            </label>
            <button type="button" onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:opacity-90 transition-all">
              <Download className="w-5 h-5" />
              {p('export_excel')}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
            </div>
          ) : paginatedEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-white/50">
              <FileText className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">{p('no_events')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full">
                <thead className="bg-white/5 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">{p('col_datetime')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">{p('col_type')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">{p('col_category')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">{p('col_description')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">{p('col_user')}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white/70 uppercase">{p('col_critical')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {paginatedEvents.map((event, index) => (
                    <tr key={event.id || index} className="hover:bg-white/5">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-300 font-mono">{getEventDisplayDate(event, i18n.language)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-white/80">{event.event_type}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(event.event_category || event.category)}`}>
                          {event.event_category || event.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-white/80 max-w-md truncate">{event.descKey ? p(event.descKey) : event.description}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-white/70">
                        <div className="flex items-center gap-2"><User className="w-4 h-4 text-white/40" />{event.userNameKey ? p(event.userNameKey) : (event.user_name || p('system_user'))}</div>
                      </td>
                      <td className="px-4 py-3 text-center">{event.is_critical ? <AlertTriangle className="w-5 h-5 text-red-400 mx-auto" /> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

// --- Inlined: EcrituresComptablesModals ---
const EMPTY_LETTER_FORM = {
  account: '',
  tolerance: '',
  dateFrom: '',
  dateTo: '',
  matchMode: '',
  letterPrefix: '',
  maxEntries: '' };

const CollapsibleSection = ({ icon: Icon, iconClass, title, subtitle, expanded, onToggle, children }) => (
  <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon size={20} className={iconClass} />
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        {subtitle}
      </div>
      {expanded ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const EcrituresComptablesModals = ({
  showEntryModal,
  setShowEntryModal,
  showImportModal,
  setShowImportModal,
  showAutoLetterModal,
  setShowAutoLetterModal,
  showTemplatesManager,
  setShowTemplatesManager,
  selectedEntry,
  setSelectedEntry,
  entryFormData,
  setEntryFormData,
  currentLine,
  setCurrentLine,
  showFiscalFields,
  setShowFiscalFields,
  onSaveEntry,
  onLaunchLettering,
  onSelectTemplate,
  accountOptions = [],
  analyticalPosteOptions = [],
  analyticalNatureOptions = [],
  analyticalUniteOptions = [],
  fiscalYearLabel = '' }) => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.ecrituresComptablesPage.${key}`, opts);
  const fmtMoney = (amount) => formatComptabiliteMoney(amount);
  const fmtDate = (value) => formatComptabiliteDate(value, i18n.language);
  const [letterFormData, setLetterFormData] = useState({ ...EMPTY_LETTER_FORM });
  const [entrySaveHint, setEntrySaveHint] = useState('');
  const [editingLineIndex, setEditingLineIndex] = useState(null);


  useEffect(() => {
    if (showEntryModal) {
      setEntrySaveHint('');
      setEditingLineIndex(null);
    }
  }, [showEntryModal]);

  useEffect(() => {
    if (showAutoLetterModal) {
      setLetterFormData({ ...EMPTY_LETTER_FORM });
    }
  }, [showAutoLetterModal]);

  const resetCurrentLine = () => {
    setCurrentLine({
      account: '',
      label: '',
      debit: '',
      credit: '',
      date_document: '',
      date_echeance: '',
      lettrage: '',
      auxiliary_code: '',
      poste_code: '',
      nature_code: '',
      unite_code: '',
      is_deductible: false,
      is_wholesale: false,
      is_fiscal_dotation: '',
      provision_type: '',
      transfer_type: '' });
    setShowFiscalFields(false);
  };

  const syncLineAmount = (value) => {
    if (value === '' || value === null || value === undefined) {
      setCurrentLine((prev) => ({ ...prev, debit: '', credit: '' }));
      return;
    }
    setCurrentLine((prev) => ({ ...prev, debit: value, credit: value }));
  };

  const tableInputClass = 'w-full min-w-0 bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-blue-500';

  const updateTableLine = (idx, patch) => {
    setEntryFormData({
      ...entryFormData,
      lines: entryFormData.lines.map((line, i) => (i === idx ? { ...line, ...patch } : line)) });
  };

  const handleTableLineAmount = (idx, side, value) => {
    if (value === '' || value === null || value === undefined) {
      updateTableLine(idx, { debit: '', credit: '' });
      return;
    }
    if (side === 'debit') {
      updateTableLine(idx, { debit: value, credit: '' });
    } else {
      updateTableLine(idx, { credit: value, debit: '' });
    }
  };

  const startEditTableLine = (idx) => {
    setEditingLineIndex(idx);
    setEntrySaveHint('');
  };

  const finishEditTableLine = (idx) => {
    const line = entryFormData.lines[idx];
    if (!isValidAccountNumber(line?.account) || !line?.label?.trim() || !hasLineAmount(line)) {
      setEntrySaveHint(p('hint_line_incomplete'));
      return;
    }
    setEditingLineIndex(null);
    setEntrySaveHint('');
  };

  const handleDeleteTableLine = (idx) => {
    setEntryFormData({
      ...entryFormData,
      lines: entryFormData.lines.filter((_, i) => i !== idx) });
    if (editingLineIndex === idx) setEditingLineIndex(null);
    else if (editingLineIndex !== null && editingLineIndex > idx) setEditingLineIndex(editingLineIndex - 1);
  };

  const handleAddLine = () => {
    setEntrySaveHint('');
    if (!currentLine.account || !currentLine.label || !hasLineAmount(currentLine)) {
      setEntrySaveHint(p('hint_fill_debit'));
      return;
    }

    const balancedPair = buildBalancedLinePair(currentLine);
    if (balancedPair) {
      setEntryFormData({
        ...entryFormData,
        lines: [...entryFormData.lines, ...balancedPair] });
      resetCurrentLine();
      return;
    }

    if (!currentLine.auxiliary_code?.trim()) {
      setEntrySaveHint(p('hint_credit_account'));
    }

    const amount = parseFloat(currentLine.debit) || parseFloat(currentLine.credit);
    const debitLine = { ...currentLine, debit: amount, credit: '' };
    const newLines = [...entryFormData.lines, debitLine];
    setEntryFormData({ ...entryFormData, lines: newLines });

    const { diff, isBalanced } = computeEntryLineTotals(newLines);
    if (!isBalanced && Math.abs(diff) > 0.01) {
      setCurrentLine({
        ...EMPTY_CURRENT_LINE,
        account: '',
        auxiliary_code: '',
        label: currentLine.label || p('counterpart_label'),
        debit: Math.abs(diff),
        credit: Math.abs(diff),
        date_document: currentLine.date_document,
        date_echeance: currentLine.date_echeance,
        lettrage: currentLine.lettrage });
    } else {
      resetCurrentLine();
    }
  };

  const lineTotals = computeEntryLineTotals(entryFormData.lines, currentLine);
  const savedLineTotals = computeEntryLineTotals(entryFormData.lines);

  const handlePrefillBalancingAmount = () => {
    if (savedLineTotals.isBalanced || entryFormData.lines.length === 0) return;
    const amount = Math.abs(savedLineTotals.diff);
    if (savedLineTotals.diff > 0) {
      setCurrentLine({
        ...currentLine,
        debit: '',
        credit: amount,
        label: currentLine.label || p('counterpart_balance') });
    } else {
      setCurrentLine({
        ...currentLine,
        credit: '',
        debit: amount,
        label: currentLine.label || p('counterpart_balance') });
    }
  };

  const handleSaveEntryClick = (e) => {
    e?.preventDefault?.();
    setEntrySaveHint('');
    const resolved = resolveEntryLinesForSave(entryFormData.lines, currentLine, p);
    if (!resolved.ok) {
      setEntrySaveHint(resolved.message);
      return;
    }
    if (onSaveEntry) {
      onSaveEntry(resolved.lines);
    } else {
      setEntrySaveHint(p('feature_not_implemented'));
      setShowEntryModal(false);
    }
  };

  const handleAccountChange = (account) => {
    setCurrentLine({ ...currentLine, account });
    const accountPrefix = account.substring(0, 2);
    setShowFiscalFields(
      accountPrefix === '60' || accountPrefix === '61' || accountPrefix === '62'
      || accountPrefix === '63' || accountPrefix === '64' || accountPrefix === '65'
      || accountPrefix === '66' || accountPrefix === '67' || accountPrefix === '68'
      || accountPrefix === '69' || accountPrefix === '70' || accountPrefix === '71'
      || accountPrefix === '74' || accountPrefix === '19' || accountPrefix === '49'
      || account.startsWith('39')
    );
  };

  const handleLaunchLettering = () => {
    if (onLaunchLettering) {
      onLaunchLettering(letterFormData);
      return;
    }
    window.alert(p('auto_letter_not_implemented'));
    setShowAutoLetterModal(false);
    setSelectedEntry(null);
  };

  return (
    <>
      {showEntryModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowEntryModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-blue-400" />
                {selectedEntry ? p('modal_edit_title') : p('modal_new_title')}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEntryModal(false);
                    setShowTemplatesManager(true);
                  }}
                  className="px-4 py-2.5 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/15 transition-all flex items-center gap-2"
                  title={p('use_template_title')}
                >
                  <FileText size={16} />
                  <span className="text-sm font-medium">{p('use_template_btn')}</span>
                </button>
                <button
                  type="submit"
                  form="ecriture-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedEntry ? p('update') : p('create_entry')}
                </button>
                <button type="button" onClick={() => setShowEntryModal(false)} className="text-white/60 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1">
              <form id="ecriture-form" onSubmit={handleSaveEntryClick} className="space-y-4">
                <EntryValidator entry={entryFormData} journalId="j_od" onValidationChange={() => {}} />


                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_accounting_date')} <span className="qawaem-required-mark">*</span></label>
                      <CustomDatePicker
                        value={entryFormData.date}
                        onChange={(dateString) => setEntryFormData({ ...entryFormData, date: dateString })}
                        placeholder={p('select_date_full')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_doc_date')}</label>
                      <CustomDatePicker
                        value={entryFormData.date_document}
                        onChange={(dateString) => setEntryFormData({ ...entryFormData, date_document: dateString })}
                        placeholder={p('select_date_full')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_due_date')}</label>
                      <CustomDatePicker
                        value={entryFormData.date_echeance}
                        onChange={(dateString) => setEntryFormData({ ...entryFormData, date_echeance: dateString })}
                        placeholder={p('select_date_full')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        {p('label_piece_number')}
                        <span className="text-orange-400 ml-1" title={p('piece_required_hint')}>⚠</span>
                      </label>
                      <input
                        type="text"
                        value={entryFormData.piece_number}
                        onChange={(e) => setEntryFormData({ ...entryFormData, piece_number: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_piece')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_fiscal_year', { defaultValue: 'السنة المالية' })}</label>
                      <input
                        type="text"
                        readOnly
                        value={fiscalYearLabel}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/80 cursor-default"
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference')} <span className="qawaem-required-mark">*</span></label>
                      <input
                        type="text"
                        value={entryFormData.reference}
                        onChange={(e) => setEntryFormData({ ...entryFormData, reference: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_reference')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_folio')}</label>
                      <input
                        type="text"
                        value={entryFormData.folio}
                        onChange={(e) => setEntryFormData({ ...entryFormData, folio: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_folio')}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_description')} <span className="qawaem-required-mark">*</span></label>
                      <textarea
                        value={entryFormData.description}
                        onChange={(e) => setEntryFormData({ ...entryFormData, description: e.target.value })}
                        placeholder={p('placeholder_description')}
                        rows={3}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="space-y-4">
                    <div className="text-sm text-white/60 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>
                        {p('debit_label')}: <span className={`font-mono ${lineTotals.isBalanced && lineTotals.totalDebit > 0 ? 'text-green-400' : 'text-green-400/80'}`}>{fmtMoney(lineTotals.totalDebit)}</span>
                      </span>
                      <span>|</span>
                      <span>
                        {p('credit_label')}: <span className={`font-mono ${lineTotals.isBalanced && lineTotals.totalCredit > 0 ? 'text-blue-400' : 'text-blue-400/80'}`}>{fmtMoney(lineTotals.totalCredit)}</span>
                      </span>
                      {lineTotals.includesPending && (
                        <span className="text-white/40 text-xs">{p('pending_line_included')}</span>
                      )}
                      {!lineTotals.isBalanced && (entryFormData.lines.length > 0 || lineTotals.includesPending) && (
                        <>
                          <span>|</span>
                          <span className="text-amber-300 font-medium">
                            {p('gap_label')}: {fmtMoney(Math.abs(lineTotals.diff))}
                            {lineTotals.diff > 0 ? p('gap_credit') : p('gap_debit')}
                          </span>
                        </>
                      )}
                      {lineTotals.isBalanced && lineTotals.totalDebit > 0 && (
                        <>
                          <span>|</span>
                          <span className="text-green-300">{p('balanced')}</span>
                        </>
                      )}
                    </div>
                    <div className="space-y-4">
                    <div className="rounded-xl border border-blue-400/20 bg-blue-500/10 px-4 py-3 text-xs text-white/70 leading-relaxed">
                      {p('balanced_entry_hint')}
                    </div>

                    {entrySaveHint && (
                      <div className="rounded-xl border border-amber-400/40 bg-amber-500/15 px-4 py-3 text-sm text-amber-100">
                        {entrySaveHint}
                      </div>
                    )}

                    {entryFormData.lines.length > 0 && (
                      <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
                        <table className="w-full">
                          <thead className="glass-table-head">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('col_account')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('col_label')}</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-white/60">{p('col_debit')}</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-white/60">{p('col_credit')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('col_due')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-white/60">{p('col_reconciliation')}</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-white/60">{p('col_action')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {entryFormData.lines.map((line, idx) => {
                              const isEditing = editingLineIndex === idx;
                              return (
                              <tr key={idx} className={isEditing ? 'bg-blue-500/10' : 'hover:bg-white/5'}>
                                <td className="px-2 py-2">
                                  {isEditing ? (
                                    <CustomSelect
                                      value={line.account || ''}
                                      onChange={(val) => updateTableLine(idx, { account: val })}
                                      options={accountOptions}
                                      placeholder={p('placeholder_debit_account')}
                                      searchable={true}
                                      creatable={true}
                                      autoSelect={false}
                                    />
                                  ) : (
                                    <span className="text-sm text-cyan-400 font-mono">{line.account || '—'}</span>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={line.label || ''}
                                      onChange={(e) => updateTableLine(idx, { label: e.target.value })}
                                      className={tableInputClass}
                                      placeholder={p('col_label')}
                                    />
                                  ) : (
                                    <span className="text-sm text-white/80">{line.label || '—'}</span>
                                  )}
                                </td>
                                <td className="px-2 py-2 text-right">
                                  {isEditing ? (
                                    <CustomNumberInput
                                      step={0.01}
                                      value={line.debit || ''}
                                      onChange={(v) => handleTableLineAmount(idx, 'debit', v)}
                                      placeholder="0.00"
                                    />
                                  ) : (
                                    <span className="text-sm text-green-400 font-mono">
                                      {parseFloat(line.debit) > 0 ? fmtMoney(parseFloat(line.debit)) : '—'}
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-2 text-right">
                                  {isEditing ? (
                                    <CustomNumberInput
                                      step={0.01}
                                      value={line.credit || ''}
                                      onChange={(v) => handleTableLineAmount(idx, 'credit', v)}
                                      placeholder="0.00"
                                    />
                                  ) : (
                                    <span className="text-sm text-blue-400 font-mono">
                                      {parseFloat(line.credit) > 0 ? fmtMoney(parseFloat(line.credit)) : '—'}
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  {isEditing ? (
                                    <CustomDatePicker
                                      value={line.date_echeance || ''}
                                      onChange={(dateString) => updateTableLine(idx, { date_echeance: dateString })}
                                      placeholder="—"
                                    />
                                  ) : (
                                    <span className="text-sm text-orange-400">
                                      {line.date_echeance ? fmtDate(line.date_echeance) : '—'}
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={line.lettrage || ''}
                                      onChange={(e) => updateTableLine(idx, { lettrage: e.target.value.toUpperCase() })}
                                      className={`${tableInputClass} font-mono`}
                                      placeholder="L001"
                                    />
                                  ) : line.lettrage ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono text-xs">
                                      {line.lettrage}
                                    </span>
                                  ) : (
                                    <span className="text-white/30 text-sm">—</span>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  <div className="flex items-center justify-center gap-1">
                                    {isEditing ? (
                                      <button
                                        type="button"
                                        onClick={() => finishEditTableLine(idx)}
                                        className="p-1.5 rounded-lg text-green-400 hover:bg-green-500/20 transition-colors"
                                        title={p('validate_line')}
                                      >
                                        <CheckCircle size={16} />
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => startEditTableLine(idx)}
                                        className="p-1.5 rounded-lg text-blue-400 hover:bg-blue-500/20 transition-colors"
                                        title={p('edit_line')}
                                      >
                                        <Edit3 size={16} />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteTableLine(idx)}
                                      className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors"
                                      title={p('delete_line')}
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );})}
                          </tbody>
                        </table>
                        <p className="px-3 py-2 text-xs text-white/40 border-t border-white/5">
                          {p('edit_line_hint')}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Ligne 1 — Compte | Auxiliaire | Libellé */}
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_debit_account')} <span className="qawaem-required-mark">*</span></label>
                        <CustomSelect
                          value={currentLine.account}
                          onChange={handleAccountChange}
                          options={accountOptions}
                          placeholder={p('placeholder_debit_account')}
                          searchable={true}
                          creatable={true}
                          autoSelect={false}
                        />
                      </div>
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_credit_account')}</label>
                        <CustomSelect
                          value={currentLine.auxiliary_code}
                          onChange={(val) => setCurrentLine({ ...currentLine, auxiliary_code: val })}
                          options={accountOptions}
                          placeholder={p('placeholder_credit_account')}
                          searchable={true}
                          creatable={true}
                          autoSelect={false}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_label')} <span className="qawaem-required-mark">*</span></label>
                        <input
                          type="text"
                          value={currentLine.label}
                          onChange={(e) => setCurrentLine({ ...currentLine, label: e.target.value })}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                          placeholder={p('placeholder_line_desc')}
                        />
                      </div>

                      {/* Ligne 2 — Débit | Crédit | Équilibrage */}
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('debit_label')}</label>
                        <CustomNumberInput
                          step={0.01}
                          value={currentLine.debit}
                          onChange={syncLineAmount}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('credit_label')}</label>
                        <CustomNumberInput
                          step={0.01}
                          value={currentLine.credit}
                          onChange={syncLineAmount}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex flex-col justify-end">
                        {!savedLineTotals.isBalanced && entryFormData.lines.length > 0 ? (
                          <button
                            type="button"
                            onClick={handlePrefillBalancingAmount}
                            className="w-full bg-amber-500/15 border border-amber-400/30 text-amber-200 px-3 py-2.5 rounded-xl text-xs font-medium hover:bg-amber-500/25 transition-all h-[46px]"
                            title={p('balance_btn_title')}
                          >
                            {p('balance_btn', { amount: fmtMoney(Math.abs(savedLineTotals.diff)) })}
                          </button>
                        ) : (
                          <div className="hidden md:block h-[46px]" aria-hidden="true" />
                        )}
                      </div>

                      <div className="md:col-span-3">
                        <p className="text-xs text-white/50">
                          {p('amount_sync_hint')}
                        </p>
                      </div>

                      {/* Ligne 3 — Date Document | Date Échéance | Code Lettrage */}
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_doc_date')}</label>
                        <CustomDatePicker
                          value={currentLine.date_document}
                          onChange={(dateString) => setCurrentLine({ ...currentLine, date_document: dateString })}
                          placeholder={p('select_date')}
                        />
                      </div>
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_due_date')}</label>
                        <CustomDatePicker
                          value={currentLine.date_echeance}
                          onChange={(dateString) => setCurrentLine({ ...currentLine, date_echeance: dateString })}
                          placeholder={p('select_date')}
                        />
                      </div>
                      <div>
                        <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reconciliation_code')}</label>
                        <input
                          type="text"
                          value={currentLine.lettrage}
                          onChange={(e) => setCurrentLine({ ...currentLine, lettrage: e.target.value.toUpperCase() })}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                          placeholder={p('placeholder_letter')}
                        />
                      </div>

                      <div className="md:col-span-3">
                        <button
                          type="button"
                          onClick={handleAddLine}
                          className="w-full bg-blue-500/20 border border-blue-400/30 text-blue-300 px-4 py-2.5 rounded-xl font-medium hover:bg-blue-500/30 transition-all flex items-center justify-center space-x-2"
                        >
                          <Plus size={16} />
                          <span>{p('add_balanced_line')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                </div>

                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_poste')}</label>
                      <CustomSelect
                        value={currentLine.poste_code}
                        onChange={(val) => setCurrentLine({ ...currentLine, poste_code: val })}
                        options={analyticalPosteOptions}
                        placeholder={p('placeholder_poste')}
                        searchable={true}
                        creatable={true}
                        autoSelect={false}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_nature')}</label>
                      <CustomSelect
                        value={currentLine.nature_code}
                        onChange={(val) => setCurrentLine({ ...currentLine, nature_code: val })}
                        options={analyticalNatureOptions}
                        placeholder={p('placeholder_nature')}
                        searchable={true}
                        creatable={true}
                        autoSelect={false}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_unite')}</label>
                      <CustomSelect
                        value={currentLine.unite_code}
                        onChange={(val) => setCurrentLine({ ...currentLine, unite_code: val })}
                        options={analyticalUniteOptions}
                        placeholder={p('placeholder_unite')}
                        searchable={true}
                        creatable={true}
                        autoSelect={false}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <p className="text-white/40 text-xs">{p('analytical_hint')}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-4">
                    {showFiscalFields ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {currentLine.account?.startsWith('6') && (
                          <div className="flex items-center space-x-3 md:col-span-3">
                            <input
                              type="checkbox"
                              id="is-deductible"
                              checked={currentLine.is_deductible}
                              onChange={(e) => setCurrentLine({ ...currentLine, is_deductible: e.target.checked })}
                              className="w-4 h-4 rounded border-white/20 bg-white/10 text-orange-500 focus:ring-2 focus:ring-orange-500"
                            />
                            <label htmlFor="is-deductible" className="text-white/80 text-sm cursor-pointer">
                              {p('fiscal_deductible')}
                            </label>
                          </div>
                        )}
                        {(currentLine.account?.startsWith('70') || currentLine.account?.startsWith('71') || currentLine.account?.startsWith('74')) && (
                          <div className="flex items-center space-x-3 md:col-span-3">
                            <input
                              type="checkbox"
                              id="is-wholesale"
                              checked={currentLine.is_wholesale}
                              onChange={(e) => setCurrentLine({ ...currentLine, is_wholesale: e.target.checked })}
                              className="w-4 h-4 rounded border-white/20 bg-white/10 text-orange-500 focus:ring-2 focus:ring-orange-500"
                            />
                            <label htmlFor="is-wholesale" className="text-white/80 text-sm cursor-pointer">
                              {p('fiscal_wholesale')}
                            </label>
                          </div>
                        )}
                        {currentLine.account?.startsWith('68') && (
                          <div className="md:col-span-3 space-y-2">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('fiscal_dotation_label')}</label>
                            <div className="flex gap-4">
                              <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                                <input
                                  type="radio"
                                  name="is_fiscal_dotation"
                                  checked={currentLine.is_fiscal_dotation === true}
                                  onChange={() => setCurrentLine({ ...currentLine, is_fiscal_dotation: true })}
                                  className="w-4 h-4"
                                />
                                {p('fiscal_dotation_fiscal')}
                              </label>
                              <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                                <input
                                  type="radio"
                                  name="is_fiscal_dotation"
                                  checked={currentLine.is_fiscal_dotation === false}
                                  onChange={() => setCurrentLine({ ...currentLine, is_fiscal_dotation: false })}
                                  className="w-4 h-4"
                                />
                                {p('fiscal_dotation_accounting')}
                              </label>
                            </div>
                          </div>
                        )}
                        {(currentLine.account?.startsWith('19') || currentLine.account?.startsWith('49') || currentLine.account?.startsWith('39')) && (
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('provision_type')}</label>
                            <CustomSelect
                              value={currentLine.provision_type}
                              onChange={(val) => setCurrentLine({ ...currentLine, provision_type: val })}
                              options={[
                                { value: 'depreciation', label: p('provision_depreciation'), isDefault: true },
                                { value: 'risk', label: p('provision_risk'), isDefault: true },
                                { value: 'charge', label: p('provision_charge'), isDefault: true },
                              ]}
                              placeholder={p('select_type')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                        )}
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('transfer_type')}</label>
                          <CustomSelect
                            value={currentLine.transfer_type}
                            onChange={(val) => setCurrentLine({ ...currentLine, transfer_type: val })}
                            options={[
                              { value: 'normal', label: p('transfer_normal'), isDefault: true },
                              { value: 'virement', label: p('transfer_virement'), isDefault: true },
                              { value: 'regularisation', label: p('transfer_regularisation'), isDefault: true },
                            ]}
                            placeholder={p('select')}
                            searchable={true}
                            creatable={true}
                            autoSelect={false}
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-white/50 text-sm">
                        {p('fiscal_fields_hint')}
                      </p>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showImportModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowImportModal(false)}
          role="presentation"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-white/20 shadow-2xl max-w-2xl w-full"
          >
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-6 border-b border-white/10 flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white">{p('import_title')}</h3>
                  <p className="text-emerald-100 text-sm mt-1">{p('import_subtitle')}</p>
                </div>
                <button type="button" onClick={() => setShowImportModal(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X className="h-6 w-6 text-white" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-emerald-500/50 transition-colors cursor-pointer">
                  <Upload className="h-16 w-16 text-white/40 mx-auto mb-4" />
                  <h4 className="text-white font-medium mb-2">{p('import_drop')}</h4>
                  <p className="text-white/60 text-sm mb-4">{p('import_click')}</p>
                  <button type="button" className="bg-emerald-500/20 text-emerald-300 px-6 py-2 rounded-lg text-sm hover:bg-emerald-500/30 transition-all">
                    {p('browse_files')}
                  </button>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <p className="text-blue-300 text-sm">
                    <AlertTriangle className="inline h-4 w-4 mr-2" />
                    {p('import_formats')}
                  </p>
                </div>
              </div>
              <div className="bg-slate-800/90 backdrop-blur-lg border-t border-white/10 p-6 flex gap-3">
                <button type="button" onClick={() => setShowImportModal(false)} className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/15 transition-all">
                  {p('cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    window.alert(p('import_not_implemented'));
                    setShowImportModal(false);
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all"
                >
                  {p('import_btn')}
                </button>
              </div>
            </motion.div>
        </div>,
        document.body
      )}

      {showAutoLetterModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowAutoLetterModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '900px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Zap className="h-6 w-6 text-purple-400" />
                {p('auto_letter_title')}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleLaunchLettering}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <Zap size={18} />
                  {p('launch_reconciliation')}
                </button>
                <button type="button" onClick={() => setShowAutoLetterModal(false)} className="text-white/60 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1">
              <form id="auto-letter-form" className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleLaunchLettering(); }}>
                {selectedEntry && (
                  <p className="text-white/60 text-sm mb-2">
                    {p('selected_entry')} <span className="text-white font-medium">{selectedEntry.reference || selectedEntry.id}</span>
                  </p>
                )}
                {!selectedEntry && (
                  <p className="text-white/60 text-sm mb-2">
                    {p('letter_hint')}
                  </p>
                )}


                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_account_to_letter')}</label>
                      <CustomSelect
                        value={letterFormData.account}
                        onChange={(val) => setLetterFormData({ ...letterFormData, account: val })}
                        options={accountOptions}
                        placeholder={p('placeholder_account')}
                        searchable={true}
                        creatable={true}
                        autoSelect={false}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tolerance')}</label>
                      <CustomNumberInput
                        step={0.01}
                        value={letterFormData.tolerance}
                        onChange={(v) => setLetterFormData({ ...letterFormData, tolerance: v })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('start_date')}</label>
                      <CustomDatePicker
                        value={letterFormData.dateFrom}
                        onChange={(dateString) => setLetterFormData({ ...letterFormData, dateFrom: dateString })}
                        placeholder={p('select')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('end_date')}</label>
                      <CustomDatePicker
                        value={letterFormData.dateTo}
                        onChange={(dateString) => setLetterFormData({ ...letterFormData, dateTo: dateString })}
                        placeholder={p('select')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_match_mode')}</label>
                      <CustomSelect
                        value={letterFormData.matchMode}
                        onChange={(val) => setLetterFormData({ ...letterFormData, matchMode: val })}
                        options={[
                          { value: 'exact', label: p('match_exact'), isDefault: true },
                          { value: 'tolerance', label: p('match_tolerance'), isDefault: true },
                          { value: 'reference', label: p('match_reference'), isDefault: true },
                        ]}
                        placeholder={p('select_mode')}
                        searchable={true}
                        creatable={true}
                        autoSelect={false}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_letter_prefix')}</label>
                      <input
                        type="text"
                        value={letterFormData.letterPrefix}
                        onChange={(e) => setLetterFormData({ ...letterFormData, letterPrefix: e.target.value.toUpperCase() })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                        placeholder={p('ph_letter_prefix')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_max_entries')}</label>
                      <CustomNumberInput
                        step={1}
                        value={letterFormData.maxEntries}
                        onChange={(v) => setLetterFormData({ ...letterFormData, maxEntries: v })}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-3">
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                        <ul className="text-white/70 text-sm space-y-1 list-disc list-inside">
                          <li>{p('letter_step_1')}</li>
                          <li>{p('letter_step_2')}</li>
                        <li>{p('letter_step_3')}</li>
                        <li>{p('letter_step_4')}</li>
                      </ul>
                    </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      <EntryTemplatesManager
        isOpen={showTemplatesManager}
        onClose={() => setShowTemplatesManager(false)}
        onSelectTemplate={(template) => {
          if (onSelectTemplate) onSelectTemplate(template);
          setShowTemplatesManager(false);
          setShowEntryModal(true);
        }}
      />
    </>
  );
};

const PieceRenumberDialog = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.ecrituresComptablesPage.${key}`, opts);
  const [renumberBy, setRenumberBy] = useState('journal');
  const [digits, setDigits] = useState(6);
  const [journalId, setJournalId] = useState('');
  const [folio, setFolio] = useState('');

  const handleRenumber = async () => {
    try {
      const endpoint = renumberBy === 'folio'
        ? '/api/comptabilite/renumber/by-folio'
        : '/api/comptabilite/renumber/by-journal';
      const body = renumberBy === 'folio' ? { folio, digits } : { journal_id: journalId, digits };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body) });
      const result = await response.json();
      if (result.success) {
        alert(t(result.messageKey) || result.message || p('error_generic'));
        onClose();
      }
    } catch (error) {
      logger.error('Renumber error:', error);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-slate-800 via-blue-900/50 to-slate-800 border-2 border-blue-400/50 rounded-2xl p-6 max-w-md w-full"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">{p('renumber_title')}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white">
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('renumber_by')}</label>
            <div className="flex space-x-3">
              <button type="button" onClick={() => setRenumberBy('journal')} className={`flex-1 px-4 py-2 rounded-xl border transition-all ${renumberBy === 'journal' ? 'bg-blue-500/30 border-blue-400/50 text-white' : 'bg-white/5 border-white/20 text-white/60 hover:bg-white/10'}`}>{p('journal')}</button>
              <button type="button" onClick={() => setRenumberBy('folio')} className={`flex-1 px-4 py-2 rounded-xl border transition-all ${renumberBy === 'folio' ? 'bg-blue-500/30 border-blue-400/50 text-white' : 'bg-white/5 border-white/20 text-white/60 hover:bg-white/10'}`}>{p('folio')}</button>
            </div>
          </div>
          <div>
            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('digit_count')}</label>
            <input type="number" min="3" max="10" value={digits} onChange={(e) => setDigits(parseInt(e.target.value, 10) || 6)} className="w-full px-4 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50" />
          </div>
          {renumberBy === 'journal' && (
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('journal')}:</label>
              <select value={journalId} onChange={(e) => setJournalId(e.target.value)} className="w-full px-4 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50">
                <option value="">{p('select_journal')}</option>
                <option value="j_ve">{p('journal_ve')}</option>
                <option value="j_ac">{p('journal_ac')}</option>
                <option value="j_bq">{p('journal_bq')}</option>
                <option value="j_ca">{p('journal_ca')}</option>
                <option value="j_od">{p('journal_od')}</option>
              </select>
            </div>
          )}
          {renumberBy === 'folio' && (
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('folio_number')}</label>
              <input type="text" value={folio} onChange={(e) => setFolio(e.target.value)} placeholder={p('ph_folio_number')} className="w-full px-4 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50" />
            </div>
          )}
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-all">{p('cancel')}</button>
            <button type="button" onClick={handleRenumber} disabled={!journalId && !folio} className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 disabled:opacity-50">
              <Play size={16} />
              <span>{p('renumber_btn')}</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

/** Map API rows to UI shape used in ComptabilitePage */
export function normalizeEntry(row) {
  if (!row) return null;

  if (row.date && row.debit && typeof row.debit === 'object') {
    return {
      ...row,
      status: normalizeEntryStatus(row.status),
      lines: row.lines?.length ? row.lines : buildLinesFromDebitCredit(row) };
  }

  const base = transformJournalEntry(row);
  let lines = [];

  if (Array.isArray(row.lines) && row.lines.length > 0) {
    lines = row.lines.map((line) => ({
      account: line.account_number || line.account || '',
      label: line.description || line.label || line.account_name || '',
      debit: line.debit || 0,
      credit: line.credit || 0,
      date_echeance: line.date_echeance || line.due_date || row.date_echeance || '',
      lettrage: line.lettrage || line.lettering_code || '',
      auxiliary_code: line.auxiliary_code || '',
      poste_code: line.poste_code || '',
      nature_code: line.nature_code || '',
      unite_code: line.unite_code || '' }));
  } else if (row.debit_account || row.credit_account) {
    if (row.debit_account) {
      lines.push({
        account: row.debit_account,
        label: row.debit_account_name || '',
        debit: row.debit_amount || 0,
        credit: 0,
        date_echeance: row.date_echeance || '',
        lettrage: row.lettrage_code || '' });
    }
    if (row.credit_account) {
      lines.push({
        account: row.credit_account,
        label: row.credit_account_name || '',
        debit: 0,
        credit: row.credit_amount || 0,
        date_echeance: row.date_echeance || '',
        lettrage: row.lettrage_code || '' });
    }
  }

  const debitLine = lines.find((l) => parseFloat(l.debit) > 0) || {};
  const creditLine = lines.find((l) => parseFloat(l.credit) > 0) || {};

  return {
    ...base,
    status: normalizeEntryStatus(base.status || row.status),
    lines,
    debit: {
      account: debitLine.account || row.debit_account || '—',
      name: debitLine.label || row.debit_account_name || '',
      amount: parseFloat(row.total_debit ?? row.debit_amount ?? debitLine.debit ?? 0) },
    credit: {
      account: creditLine.account || row.credit_account || '—',
      name: creditLine.label || row.credit_account_name || '',
      amount: parseFloat(row.total_credit ?? row.credit_amount ?? creditLine.credit ?? 0) },
    letteringCode: row.lettering_code || lines.find((l) => l.lettrage)?.lettrage || null,
    entry_number: row.entry_number };
}

function buildLinesFromDebitCredit(entry) {
  const lines = [];
  if (entry.debit?.account) {
    lines.push({
      account: entry.debit.account,
      label: entry.debit.name || '',
      debit: entry.debit.amount || 0,
      credit: 0,
      date_echeance: entry.date_echeance || '',
      lettrage: entry.letteringCode || '' });
  }
  if (entry.credit?.account) {
    lines.push({
      account: entry.credit.account,
      label: entry.credit.name || '',
      debit: 0,
      credit: entry.credit.amount || 0,
      date_echeance: entry.date_echeance || '',
      lettrage: entry.letteringCode || '' });
  }
  return lines;
}

const EMPTY_ENTRY_FORM = {
  date: '',
  date_document: '',
  date_echeance: '',
  reference: '',
  description: '',
  folio: '',
  piece_number: '',
  lines: [] };

const normalizeEntryStatus = (status) => {
  if (!status || status === 'draft' || status === 'pending') return 'brouillon';
  if (status === 'validated') return 'validé';
  return status;
};

const EMPTY_CURRENT_LINE = {
  account: '',
  label: '',
  debit: '',
  credit: '',
  date_document: '',
  date_echeance: '',
  lettrage: '',
  auxiliary_code: '',
  poste_code: '',
  nature_code: '',
  unite_code: '',
  is_deductible: false,
  is_wholesale: false,
  is_fiscal_dotation: '',
  provision_type: '',
  transfer_type: '' };

const ECRITURES_LIST_COLUMNS = [
  { key: 'date', labelKey: 'col_date' },
  { key: 'reference', labelKey: 'col_reference' },
  { key: 'description', labelKey: 'col_description' },
  { key: 'debit', labelKey: 'col_debit' },
  { key: 'credit', labelKey: 'col_credit' },
  { key: 'due', labelKey: 'col_due' },
  { key: 'reconciliation', labelKey: 'col_reconciliation' },
  { key: 'status', labelKey: 'col_status' },
];

const EcrituresComptablesPage = () => {
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  useComptabiliteConfig();
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.ecrituresComptablesPage.${key}`, opts);
  const datePickerLocale = getComptabiliteDatePickerLocale();
  const fmtMoney = (amount) => formatComptabiliteMoney(amount);
  const fmtDate = (value) => formatComptabiliteDate(value, i18n.language);

  const [entries, setEntries] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [analyticalPosteOptions, setAnalyticalPosteOptions] = useState([]);
  const [analyticalNatureOptions, setAnalyticalNatureOptions] = useState([]);
  const [analyticalUniteOptions, setAnalyticalUniteOptions] = useState([]);
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('date', (row) => row.date ?? ''),
    textColumnRule('reference', (row) => row.reference ?? ''),
    textColumnRule('description', (row) => row.description ?? ''),
    textColumnRule('debit', (row) => String(row.debit?.amount ?? '')),
    textColumnRule('credit', (row) => String(row.credit?.amount ?? '')),
    textColumnRule('status', (row) => row.status ?? ''),
  ], []);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_entries');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAutoLetterModal, setShowAutoLetterModal] = useState(false);
  const [showTemplatesManager, setShowTemplatesManager] = useState(false);
  const [showPieceRenumberDialog, setShowPieceRenumberDialog] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);

  const [entryFilters, setEntryFilters] = useState({
    echeanceFrom: '',
    echeanceTo: '',
    lettrageStatus: 'all' });

  const [entryFormData, setEntryFormData] = useState({ ...EMPTY_ENTRY_FORM });
  const [currentLine, setCurrentLine] = useState({ ...EMPTY_CURRENT_LINE });
  const [showFiscalFields, setShowFiscalFields] = useState(false);

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);


  useEffect(() => {
    loadEntries();
    loadChartAccounts();
    loadAnalyticalOptions();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);

  const loadAnalyticalOptions = async () => {
    try {
      const opts = await loadAnalyticalSelectOptions();
      setAnalyticalPosteOptions(opts.postes);
      setAnalyticalNatureOptions(opts.natures);
      setAnalyticalUniteOptions(opts.unites);
    } catch (error) {
      console.error('Error loading analytical codes:', error);
      setAnalyticalPosteOptions([]);
      setAnalyticalNatureOptions([]);
      setAnalyticalUniteOptions([]);
    }
  };

  useEffect(() => {
    const onChartRefresh = () => { void loadChartAccounts(); };
    const onAnalyticalRefresh = () => { void loadAnalyticalOptions(); };
    window.addEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onChartRefresh);
    window.addEventListener(ANALYTICAL_CODES_REFRESH_EVENT, onAnalyticalRefresh);
    return () => {
      window.removeEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onChartRefresh);
      window.removeEventListener(ANALYTICAL_CODES_REFRESH_EVENT, onAnalyticalRefresh);
    };
  }, []);

  const loadChartAccounts = async () => {
    try {
      const rows = await getChartOfAccounts();
      setChartAccounts(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading chart accounts:', error);
      setChartAccounts([]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await getJournalEntries();
      setEntries(sortRecordsNewestFirst((Array.isArray(data) ? data : []).map(normalizeEntry).filter(Boolean)));
    } catch (error) {
      console.error('Error loading journal entries:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const matchesEntryFilters = (entry) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!entry.reference?.toLowerCase().includes(term) && !entry.description?.toLowerCase().includes(term)) {
        return false;
      }
    }
    if (statusFilter !== 'all' && entry.status !== statusFilter) return false;

    if (entryFilters.echeanceFrom || entryFilters.echeanceTo) {
      const hasMatchingEcheance = entry.lines?.some((line) => {
        if (!line.date_echeance) return false;
        if (entryFilters.echeanceFrom && new Date(line.date_echeance) < new Date(entryFilters.echeanceFrom)) return false;
        if (entryFilters.echeanceTo && new Date(line.date_echeance) > new Date(entryFilters.echeanceTo)) return false;
        return true;
      });
      if (!hasMatchingEcheance) return false;
    }

    if (entryFilters.lettrageStatus === 'lettrees') {
      const hasLettrage = entry.lines?.some((line) => line.lettrage);
      if (!hasLettrage) return false;
    }
    if (entryFilters.lettrageStatus === 'non_lettrees') {
      const hasNonLettrage = entry.lines?.some((line) => !line.lettrage && (line.debit || line.credit));
      if (!hasNonLettrage) return false;
    }

    if (quickFilter === 'validé' && entry.status !== 'validé') return false;
    if (quickFilter === 'brouillon' && entry.status !== 'brouillon') return false;
    if (quickFilter === 'to_letter') {
      const needsLetter = entry.lines?.some((line) => !line.lettrage && (line.debit || line.credit));
      if (!needsLetter) return false;
    }

    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_entries':
        case 'by_status':
          if (activeDistributionFilter === 'validated' && entry.status !== 'validé') return false;
          if (activeDistributionFilter === 'draft' && entry.status !== 'brouillon') return false;
          if (activeDistributionFilter === 'closed' && entry.status !== 'clôturé') return false;
          if (activeDistributionFilter === 'to_reconcile') {
            const needs = entry.lines?.some((line) => !line.lettrage && (line.debit || line.credit));
            if (!needs) return false;
          }
          break;
        case 'by_type':
          if (activeDistributionFilter === 'purchases' && entry.journalType !== 'achats' && entry.journalType !== 'AC') return false;
          if (activeDistributionFilter === 'sales' && entry.journalType !== 'ventes' && entry.journalType !== 'VE') return false;
          if (activeDistributionFilter === 'bank' && entry.journalType !== 'banque' && entry.journalType !== 'BQ') return false;
          if (activeDistributionFilter === 'od' && entry.journalType !== 'operations_diverses' && entry.journalType !== 'OD') return false;
          break;
        default:
          break;
      }
    }

    return true;
  };

  const filteredEntries = useMemo(() => {
    const base = entries.filter(matchesEntryFilters);
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [entries, searchTerm, statusFilter, entryFilters, quickFilter, activeDistributionFilter, mainViewMode, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredEntries, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;

  useEffect(() => {
    if (selectedRecords.length > 0) setIsStatusFilterOpen(false);
  }, [selectedRecords.length]);
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(ECRITURES_LIST_COLUMNS, p);
  const ecrituresTableColSpan = visibleDataColumnCount + 2;

  const stats = useMemo(() => {
    const validees = entries.filter((e) => e.status === 'validé').length;
    const brouillon = entries.filter((e) => e.status === 'brouillon').length;
    const totalDebit = entries.reduce((sum, e) => sum + (parseFloat(e.debit?.amount) || 0), 0);
    const totalCredit = entries.reduce((sum, e) => sum + (parseFloat(e.credit?.amount) || 0), 0);
    const aLettrer = entries.filter((e) => e.lines?.some((line) => !line.lettrage && (line.debit || line.credit))).length;
    return { total: entries.length, validees, brouillon, totalDebit, totalCredit, aLettrer };
  }, [entries]);

  const generateEntryReference = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'journalEntry', 'ECR');
    return generateEntityReference(prefix, entries, (entry) => entry.reference);
  }, [entries, settings]);

  const handleBulkDelete = async () => {
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) return;
    try {
      await Promise.all(selectedRecords.map((id) => deleteJournalEntry(id)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label_plural', { defaultValue: 'قيود' }));
      setSelectedRecords([]);
      clearSelection();
        loadEntries();
    } catch (error) {
      console.error('Error deleting entries:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('confirm_delete'))) return;
    try {
      await deleteJournalEntry(id);
      notifyDeleted(showNotify, p('entity_label', { defaultValue: 'القيد' }));
      loadEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message }));
    }
  };

  const accountOptions = useMemo(
    () => buildChartAccountOptions(chartAccounts),
    [chartAccounts],
  );

  const openNewEntry = () => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedEntry(null);
    setEntryFormData({ ...EMPTY_ENTRY_FORM, date: today, reference: generateEntryReference(), lines: [] });
    setCurrentLine({ ...EMPTY_CURRENT_LINE });
    setShowEntryModal(true);
  };

  const openEditEntry = async (entry) => {
    try {
      setLoading(true);
      const fullEntry = await getJournalEntry(entry.id);
      const normalized = normalizeEntry(fullEntry) || entry;
      setSelectedEntry(normalized);
      setEntryFormData({
        date: normalized.date ? String(normalized.date).split('T')[0] : '',
        date_document: normalized.date_document || '',
        date_echeance: normalized.date_echeance || '',
        reference: normalized.reference || '',
        description: normalized.description || '',
        folio: normalized.folio || '',
        piece_number: normalized.piece_number || normalized.entry_number || '',
        lines: normalized.lines || [] });
      setCurrentLine({ ...EMPTY_CURRENT_LINE });
      setShowEntryModal(true);
    } catch (error) {
      console.error('Error loading entry for edit:', error);
      window.alert(p('alert_load_error') || t('common.error_generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEntry = async (linesOverride) => {
    const lines = Array.isArray(linesOverride) ? linesOverride : entryFormData.lines;
    if (!entryFormData.date) {
      window.alert(p('alert_date_required'));
      return;
    }
    if (!entryFormData.reference?.trim()) {
      window.alert(p('alert_reference_required'));
      return;
    }
    if (!entryFormData.description?.trim()) {
      window.alert(p('alert_description_required'));
      return;
    }
    if (!lines?.length) {
      window.alert(p('alert_no_lines'));
      return;
    }

    const invalidLineIndex = lines.findIndex((line) => !isValidAccountNumber(line.account || line.account_number));
    if (invalidLineIndex !== -1) {
      window.alert(p('error_invalid_account', { li: invalidLineIndex + 1 }));
      return;
    }

    const { isBalanced, totalDebit, totalCredit, diff } = computeEntryLineTotals(lines);
    if (!isBalanced) {
      window.alert(p('error_unbalanced', {
        debit: fmtMoney(totalDebit),
        credit: fmtMoney(totalCredit),
        diff: fmtMoney(Math.abs(diff)),
        side: diff > 0 ? p('error_side_credit') : p('error_side_debit') }));
      return;
    }

    try {
      const reference = (entryFormData.reference || '').trim() || generateEntryReference();
      const payload = { ...entryFormData, reference, lines };
      if (selectedEntry?.id) {
        await updateJournalEntry(selectedEntry.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'القيد' }));
      } else {
        await createJournalEntry(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'القيد' }));
      }
      setShowEntryModal(false);
      setSelectedEntry(null);
      setEntryFormData({ ...EMPTY_ENTRY_FORM, lines: [] });
      setCurrentLine({ ...EMPTY_CURRENT_LINE });
      await loadEntries();
      await loadChartAccounts();
    } catch (error) {
      console.error('Error saving entry:', error);
      notifyError(showNotify, p('alert_save_error') || t('common.error_generic'));
    }
  };

  const handleLaunchLettering = async (letterFormData) => {
    if (!letterFormData?.account?.trim()) {
      window.alert(p('alert_select_account'));
      return;
    }

    try {
      const result = await applyAutoLettrage(letterFormData.account, {
        tolerance: letterFormData.tolerance || '0.01',
        letterPrefix: letterFormData.letterPrefix || 'L' });

      window.alert(t(result.messageKey) || result.message || p('alert_reconcile_done'));
      setShowAutoLetterModal(false);
      setSelectedEntry(null);
      await loadEntries();
    } catch (error) {
      console.error('Auto-lettrage error:', error);
      window.alert(p('alert_reconcile_error') || t('common.error_generic'));
    }
  };

  const renderColumnFilter = (label, key, center = false) => (
    <TableColumnHeaderFilter
      columnKey={key}
      label={label}
      value={columnFilters[key] || ''}
      onChange={(v) => setColumnFilter(key, v)}
      editingKey={editingKey}
      onEditingKeyChange={setEditingKey}
      align={center ? 'center' : 'start'}
      thClassName="text-white"
    />
  );

  const statusLabel = (status) => {
    if (status === 'validé') return p('status_validated');
    if (status === 'brouillon') return p('status_draft');
    if (status === 'clôturé') return p('status_closed');
    return status || '—';
  };

  const entryDistribution = useMemo(() => ({
    all_entries: {
      title: p('dist_overview'),
      data: [
        { id: 'validated', name: p('dist_validated'), count: stats.validees, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'draft', name: p('dist_draft'), count: stats.brouillon, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'to_reconcile', name: p('dist_to_reconcile'), count: stats.aLettrer, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
      ] },
    by_type: {
      title: p('dist_by_journal'),
      data: [
        { id: 'purchases', name: p('dist_purchases'), count: entries.filter((e) => e.journalType === 'achats' || e.journalType === 'AC').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'sales', name: p('dist_sales'), count: entries.filter((e) => e.journalType === 'ventes' || e.journalType === 'VE').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'bank', name: p('dist_bank'), count: entries.filter((e) => e.journalType === 'banque' || e.journalType === 'BQ').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { id: 'od', name: p('dist_od'), count: entries.filter((e) => e.journalType === 'operations_diverses' || e.journalType === 'OD').length, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
      ] },
    by_status: {
      title: p('dist_by_status'),
      data: [
        { id: 'validated', name: p('dist_validated'), count: stats.validees, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'draft', name: p('dist_draft'), count: stats.brouillon, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'closed', name: p('dist_closed'), count: entries.filter((e) => e.status === 'clôturé').length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
      ] } }), [p, stats, entries]);

  const mainTabs = useMemo(() => [
    { id: 'all_entries', name: p('tab_general'), icon: LayoutGrid },
    { id: 'by_type', name: p('tab_by_journal'), icon: FileText },
    { id: 'by_status', name: p('tab_by_status'), icon: Shield },
  ], [p]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'brouillon', name: p('filter_draft'), icon: Edit3 },
    { id: 'validé', name: p('filter_validated'), icon: CheckCircle },
    { id: 'to_letter', name: p('filter_to_letter'), icon: Link },
  ], [p]);

  const distributionInfo = entryDistribution[mainViewMode] || entryDistribution.all_entries;

  const footerTotals = useMemo(() => ({
    debit: filteredEntries.reduce((sum, e) => sum + (parseFloat(e.debit?.amount) || 0), 0),
    credit: filteredEntries.reduce((sum, e) => sum + (parseFloat(e.credit?.amount) || 0), 0) }), [filteredEntries]);

  const qawaemStats = useMemo(() => sliceQawaemStats(
    [
      { label: p('stat_total'), value: stats.total, icon: BookOpen },
      { label: p('stat_validated'), value: stats.validees, icon: CheckCircle },
      { label: p('stat_draft'), value: stats.brouillon, icon: Edit3 },
      { label: p('stat_debit_credit'), value: `${fmtMoney(stats.totalDebit)} / ${fmtMoney(stats.totalCredit)}`, icon: DollarSign },
    ].map((s, i) => ({
      key: s.label,
      label: s.label,
      value: s.value,
      icon: s.icon,
      variant: i % 2 ? 'forest' : 'copper' })),
    4,
  ), [stats, p, fmtMoney]);

  const quickFilterLabel = quickFilterButtons.find((b) => b.id === quickFilter)?.name || p('filter_all');

  return (
    <QawaemErpListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={BookOpen}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={(
        <ComptabiliteBulkDeleteFilterAction
          count={selectedRecords.length}
          onClick={handleBulkDelete}
          label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
          title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
        />
      )}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={openNewEntry}
      createLabel={p('new_entry')}
      headerActions={(
        <>
          <button type="button" onClick={loadEntries} className="refresh-button" disabled={loading} aria-label={p('refresh', { defaultValue: 'تحديث' })}>
            <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => setShowAutoLetterModal(true)}
            className="bg-purple-500/20 border border-purple-500/30 text-purple-300 px-4 py-3 rounded-xl font-semibold hover:bg-purple-500/30 transition-all duration-300 flex items-center space-x-2"
            title={p('auto_reconciliation_title')}
          >
            <Zap size={18} />
            <span className="hidden sm:inline">{p('auto_reconciliation')}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowPieceRenumberDialog(true)}
            className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-4 py-3 rounded-xl font-semibold hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
          >
            <Hash size={18} />
            <span className="hidden sm:inline">{p('renumbering')}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowTemplatesManager(true)}
            className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-4 py-3 rounded-xl font-semibold hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
          >
            <FileText size={18} />
            <span className="hidden sm:inline">{p('templates')}</span>
          </button>
        </>
      )}
    >
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {mainViewMode === 'all_entries' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setQuickFilter(filter.id)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button
                  type="button"
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.id ? null : item.id)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.id ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.id ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div
                      className={`h-3 rounded-full ${item.color} transition-all`}
                      style={{
                        width: `${(() => {
                          const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                          return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                        })()}%` }}
                    />
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.id ? 'text-white/80' : 'text-white/60'}`}>
                    {(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      const percent = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      return p('percent_of_total', { percent });
                    })()}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        

        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('date') ? renderColumnFilter(p('col_date'), 'date') : null}
                  {isColVisible('reference') ? renderColumnFilter(p('col_reference'), 'reference') : null}
                  {isColVisible('description') ? renderColumnFilter(p('col_description'), 'description') : null}
                  {isColVisible('debit') ? renderColumnFilter(p('col_debit'), 'debit') : null}
                  {isColVisible('credit') ? renderColumnFilter(p('col_credit'), 'credit') : null}
                  {isColVisible('due') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_due')}</th>
                  ) : null}
                  {isColVisible('reconciliation') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_reconciliation')}</th>
                  ) : null}
                  {isColVisible('status') ? renderColumnFilter(p('col_status'), 'status', true) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredEntries.length > 0 ? filteredEntries.map((entry, index) => (
                  <tr
                    key={entry.id}
                    className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(entry.id) ? 'bg-white/10' : ''}`}
                  >
                    <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(entry.id)}
                              onChange={() => handleSelectRecord(entry.id)}
                            />
                          </div>
                        </td>
                    {isColVisible('date') ? (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-white font-semibold">
                        <Clock size={14} className="text-white/50" />
                        {entry.date ? fmtDate(entry.date) : '—'}
                      </div>
                    </td>
                    ) : null}
                    {isColVisible('reference') ? (
                    <td className="px-6 py-4 text-white font-semibold">{entry.reference || '—'}</td>
                    ) : null}
                    {isColVisible('description') ? (
                    <td className="px-6 py-4 text-white">{entry.description || '—'}</td>
                    ) : null}
                    {isColVisible('debit') ? (
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span className="text-cyan-400 text-sm font-mono">{entry.debit?.account}</span>
                        <br />
                        <span className="text-white/60 text-sm">{entry.debit?.name}</span>
                        <br />
                        <span className="text-white font-medium">{fmtMoney(entry.debit?.amount || 0)}</span>
                      </div>
                    </td>
                    ) : null}
                    {isColVisible('credit') ? (
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span className="text-cyan-400 text-sm font-mono">{entry.credit?.account}</span>
                        <br />
                        <span className="text-white/60 text-sm">{entry.credit?.name}</span>
                        <br />
                        <span className="text-white font-medium">{fmtMoney(entry.credit?.amount || 0)}</span>
                      </div>
                    </td>
                    ) : null}
                    {isColVisible('due') ? (
                    <td className="px-6 py-4">
                      {(() => {
                        const lineDates = entry.lines?.filter((l) => l.date_echeance).map((l) => l.date_echeance) || [];
                        const dates = lineDates.length > 0 ? lineDates : (entry.date_echeance ? [entry.date_echeance] : []);
                        if (dates.length === 0) return <span className="text-white/30">—</span>;
                        return dates.map((dateValue, idx) => (
                          <div key={idx} className="text-xs text-orange-400">
                            {fmtDate(dateValue)}
                          </div>
                        ));
                      })()}
                    </td>
                    ) : null}
                    {isColVisible('reconciliation') ? (
                    <td className="px-6 py-4">
                      {entry.lines?.filter((l) => l.lettrage).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {[...new Set(entry.lines.filter((l) => l.lettrage).map((l) => l.lettrage))].map((code) => (
                            <span key={code} className="inline-flex px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono text-xs">{code}</span>
                          ))}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setSelectedEntry(entry); setShowAutoLetterModal(true); }}
                          className="text-purple-400 hover:text-purple-300 text-xs flex items-center gap-1"
                          title={p('reconcile_entry')}
                        >
                          <Unlink size={16} />
                        </button>
                      )}
                    </td>
                    ) : null}
                    {isColVisible('status') ? (
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${entry.status === 'validé' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                        {statusLabel(entry.status)}
                      </span>
                    </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                            onView={() => { setViewingEntry(entry); setShowViewModal(true); }}
                            onEdit={() => openEditEntry(entry)}
                            onDelete={() => handleDelete(entry.id)}
                          />
                    </QawaemTableStickyActionsTd>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={ecrituresTableColSpan} className="text-center py-12 text-white/60">
                      <BookOpen size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={ecrituresTableColSpan} className="px-6 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-4 text-white/70 text-sm">
                      <span>{p('footer_count', { count: filteredEntries.length })}</span>
                      <span>{p('footer_total_debit')} <span className="text-white font-semibold">{fmtMoney(footerTotals.debit)}</span></span>
                      <span>{p('footer_total_credit')} <span className="text-white font-semibold">{fmtMoney(footerTotals.credit)}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredEntries.length > 0 ? filteredEntries.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-4 mb-4">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <BookOpen size={20} className="text-amber-400" />
                        {entry.reference || '—'}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${entry.status === 'validé' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                        {statusLabel(entry.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_general')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('col_date')}:</span> {entry.date ? fmtDate(entry.date) : '—'}</div>
                          <div><span className="font-medium">{p('card_reference')}</span> {entry.reference || '—'}</div>
                          <div><span className="font-medium">{p('card_journal')}</span> {entry.journalType || '—'}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('debit_label')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div className="font-mono text-cyan-400">{entry.debit?.account}</div>
                          <div>{entry.debit?.name}</div>
                          <div className="text-green-300 font-semibold">{fmtMoney(entry.debit?.amount || 0)}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('credit_label')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div className="font-mono text-cyan-400">{entry.credit?.account}</div>
                          <div>{entry.credit?.name}</div>
                          <div className="text-blue-300 font-semibold">{fmtMoney(entry.credit?.amount || 0)}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_due_reconcile')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('card_description')}</span> {entry.description || '—'}</div>
                          <div>
                            <span className="font-medium">{p('card_reconciliation')}</span>{' '}
                            {entry.lines?.filter((l) => l.lettrage).length > 0
                              ? [...new Set(entry.lines.filter((l) => l.lettrage).map((l) => l.lettrage))].join(', ')
                              : p('not_reconciled')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <QawaemErpRowActions
                    compact
                    size={14}
                    onView={() => { setViewingEntry(entry); setShowViewModal(true); }}
                    onEdit={() => openEditEntry(entry)}
                    onDelete={() => handleDelete(entry.id)}
                  />
                </div>
              </motion.div>
            )) : (
              <div className="col-span-full text-center py-12">
                <BookOpen size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <EcrituresComptablesModals
        showEntryModal={showEntryModal}
        setShowEntryModal={setShowEntryModal}
        showImportModal={showImportModal}
        setShowImportModal={setShowImportModal}
        showAutoLetterModal={showAutoLetterModal}
        setShowAutoLetterModal={setShowAutoLetterModal}
        showTemplatesManager={showTemplatesManager}
        setShowTemplatesManager={setShowTemplatesManager}
        selectedEntry={selectedEntry}
        setSelectedEntry={setSelectedEntry}
        entryFormData={entryFormData}
        setEntryFormData={setEntryFormData}
        currentLine={currentLine}
        setCurrentLine={setCurrentLine}
        showFiscalFields={showFiscalFields}
        setShowFiscalFields={setShowFiscalFields}
        onSaveEntry={handleSaveEntry}
        onLaunchLettering={handleLaunchLettering}
        onSelectTemplate={() => openNewEntry()}
        accountOptions={accountOptions}
        analyticalPosteOptions={analyticalPosteOptions}
        analyticalNatureOptions={analyticalNatureOptions}
        analyticalUniteOptions={analyticalUniteOptions}
        fiscalYearLabel={fiscalYearLabel}
      />

      <PieceRenumberDialog isOpen={showPieceRenumberDialog} onClose={() => setShowPieceRenumberDialog(false)} />

      {showViewModal && viewingEntry && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4)', width: '95%', maxWidth: '900px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-amber-400" />
                {p('view_details_title')}
              </h2>
              <button type="button" onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white"><X size={24} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-white/80">
              <div><span className="text-white/50 text-sm">{p('col_date')}</span><p className="font-semibold text-white">{viewingEntry.date ? fmtDate(viewingEntry.date) : '—'}</p></div>
              <div><span className="text-white/50 text-sm">{p('col_reference')}</span><p className="font-semibold text-white">{viewingEntry.reference}</p></div>
              <div className="md:col-span-3"><span className="text-white/50 text-sm">{p('label_description')}</span><p className="font-semibold text-white">{viewingEntry.description}</p></div>
              <div><span className="text-white/50 text-sm">{p('debit_label')}</span><p className="font-semibold text-green-300">{fmtMoney(viewingEntry.debit?.amount || 0)} — {viewingEntry.debit?.account}</p></div>
              <div><span className="text-white/50 text-sm">{p('credit_label')}</span><p className="font-semibold text-blue-300">{fmtMoney(viewingEntry.credit?.amount || 0)} — {viewingEntry.credit?.account}</p></div>
              <div><span className="text-white/50 text-sm">{p('status')}</span><p className="font-semibold text-white">{statusLabel(viewingEntry.status)}</p></div>
            </div>
            <button type="button" onClick={() => setShowViewModal(false)} className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10">{p('close')}</button>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export { JournalElectroniqueViewer };
export default EcrituresComptablesPage;

